import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";

export default function ProtectedRoute({ children, rol, allowedRoles }) {
  const { user, rol: userRol, userStatus, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectingToLogin = useRef(false);
  const redirectingPendiente = useRef(false);

  useEffect(() => {
    if (loading || user) {
      redirectingToLogin.current = false;
      return;
    }
    if (redirectingToLogin.current) return;
    if (location.pathname === "/login") return; // Evitar navegación redundante (crbug.com/1038223)
    redirectingToLogin.current = true;
    navigate("/login", { replace: true });
  }, [loading, user, navigate, location.pathname]);

  // Usuario sin tipo definido: redirigir a /select-account-type
  // null = rol aún no resuelto (race condition al cargar) → no redirigir, esperar
  useEffect(() => {
    if (loading || !user || redirectingPendiente.current) return;
    if (userRol === "pendiente_tipo") {
      if (location.pathname === "/select-account-type") return;
      redirectingPendiente.current = true;
      navigate("/select-account-type", { replace: true });
      return;
    }
  }, [loading, user, userRol, navigate, location.pathname]);

  // Cliente o empresa: solo permitir dashboard si status === 'activo'. Cualquier otro valor (null, pendiente, enviada, etc.) → /pendiente
  useEffect(() => {
    if (loading || !user || redirectingPendiente.current) return;
    if (userRol === "cliente" && userStatus !== "activo" && userStatus !== null) {
      if (location.pathname === "/pendiente") return;
      redirectingPendiente.current = true;
      navigate("/pendiente", { replace: true });
      return;
    }
    // null = sin campo activo/estado (usuario legado) → dejar pasar
    if ((userRol === "empresa" || userRol === "proveedor") && userStatus !== "activo" && userStatus !== null) {
      if (location.pathname === "/empresa/pendiente") return;
      redirectingPendiente.current = true;
      navigate("/empresa/pendiente", { replace: true });
    }
  }, [loading, user, userRol, userStatus, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Redirigiendo a inicio de sesión...</div>
      </div>
    );
  }

  // Rol aún no resuelto (AuthProvider cargando doc de Firestore) → spinner en lugar de "Acceso Denegado"
  if (userRol === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (userRol === "cliente" && userStatus !== "activo" && userStatus !== null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Redirigiendo...</div>
      </div>
    );
  }
  if ((userRol === "empresa" || userRol === "proveedor") && userStatus !== "activo" && userStatus !== null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Redirigiendo...</div>
      </div>
    );
  }

  // Si se especifica allowedRoles, verificar si el rol del usuario está en la lista
  if (allowedRoles && Array.isArray(allowedRoles)) {
    if (!allowedRoles.includes(userRol)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600 mb-2">Acceso Denegado</h2>
            <p className="text-gray-600 mb-4">
              Se requiere uno de los roles: {allowedRoles.join(', ')} pero tienes rol '{userRol || 'ninguno'}'
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Volver
            </button>
          </div>
        </div>
      );
    }
  } else if (rol && userRol !== rol) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600 mb-4">
            Se requiere rol '{rol}' pero tienes rol '{userRol || 'ninguno'}'
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return children;
}